import { fetchAllPages } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import { FunctionalityClient } from '@frontend/functionality/api';
import { Functionality } from '@frontend/functionality/types';
import { useAccountRepository } from '@frontend/repository';
import { SettingClient } from '@frontend/setting/api';
import { Setting } from '@frontend/setting/types';
import { StepClient, WizardClient } from '@frontend/wizard/api';
import { clearWizards, initialiseWizard, updateWizardSteps } from '@frontend/wizard/mobile';
import { Step, Wizard } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { useLicenseRepository } from '../../utils/license-repository';
import { resolveNavigationWizardData } from '../utils/resolve-wizard-package';

interface CacheEntry {
    functionality: Functionality | null;
    functionalitySettings: Setting[];
    wizard: Wizard;
    steps: Step[];
    timestamp: number;
}

const wizardCache = new Map<string, CacheEntry>();

const useWizardInitialisation = () => {
    const dispatch = useAppDispatch();
    const navigate = useKioskNavigate();
    const { state: locationState } = useLocation();
    const [searchParams] = useSearchParams();
    const { currentAccount } = useAccountRepository();
    const { functionalityId, wizardId } = useParams<{ functionalityId: string; wizardId: string }>();
    const { allFunctionalities } = useLicenseRepository();
    const wizardStore = useAppSelector((state) => state.wizards);
    const [functionality, setFunctionality] = useState<Functionality | null>(null);
    const [functionalitySettings, setFunctionalitySettings] = useState<Setting[] | null>(null);
    const [wizard, setWizard] = useState<Wizard | null>(null);
    const [steps, setSteps] = useState<Step[] | null>(null);
    const [from, setFrom] = useState<string | undefined>(undefined);

    useEffect(() => {
        dispatch(clearWizards());
    }, []);

    useEffect(() => {
        if (!functionalityId || !wizardId || !currentAccount) return;

        const cacheKey = `${functionalityId}-${wizardId}`;
        const cachedData = wizardCache.get(cacheKey);

        // Use cached data immediately if available
        if (cachedData && cachedData.functionality && cachedData.wizard && cachedData.steps && cachedData.functionalitySettings) {
            setFunctionality(cachedData.functionality);
            setFunctionalitySettings(cachedData.functionalitySettings);
            setWizard(cachedData.wizard);
            setSteps(cachedData.steps);
        }

        // Fetch by known URL ids — do not wait for the full functionalities list.
        const fetchFreshData = async () => {
            const functionality =
                allFunctionalities.find((f) => f.id === functionalityId) ??
                (await FunctionalityClient.fetchFunctionality(currentAccount, functionalityId).catch(() => null));
            if (!functionality) return;

            const [settingsRes, wizard, steps] = await Promise.all([
                SettingClient.fetchSettings({ entity_type: EntityType.FUNCTIONALITY, entity_id: functionalityId }),
                WizardClient.fetchWizard(currentAccount, wizardId),
                fetchAllPages((params) => StepClient.fetchSteps(params), {
                    account_id: currentAccount,
                    wizard_id: wizardId
                })
            ]);

            const freshData: CacheEntry = {
                functionality,
                functionalitySettings: settingsRes.results,
                wizard,
                steps,
                timestamp: Date.now()
            };

            wizardCache.set(cacheKey, freshData);

            setFunctionality(freshData.functionality);
            setFunctionalitySettings(freshData.functionalitySettings);
            setWizard(freshData.wizard);
            setSteps(freshData.steps);
        };

        void fetchFreshData();

        const from = searchParams.get('from');
        if (from) setFrom(decodeURIComponent(from));
    }, [allFunctionalities, currentAccount, functionalityId, searchParams, wizardId]);

    useEffect(() => {
        if (functionality && functionalitySettings && wizard && steps) {
            const existing = wizardStore[wizard.id];
            if (!existing) {
                dispatch(
                    initialiseWizard({
                        key: wizard.id,
                        init: {
                            functionality,
                            functionalitySettings,
                            wizard,
                            steps,
                            from,
                            data: resolveNavigationWizardData(locationState)
                        }
                    })
                );
            } else {
                // Cache may hydrate a truncated first page before fetchAllPages finishes.
                if (existing.steps.length !== steps.length) {
                    dispatch(updateWizardSteps({ key: wizard.id, steps }));
                }
                navigate(`./screens/${wizard.start_screen}`, { state: locationState });
            }
        }
    }, [wizardStore, functionality, functionalitySettings, wizard, steps]);
};

export default useWizardInitialisation;
